import { defineMessages } from 'react-intl';

export const addSpaceBeforeAsterisks = (str) => str.replace(/\*+/g, match => ' ' + match);

export const roles = {
  ADMIN: 'ADMIN',
  SECONDARY_OFFICER: 'SECONDARY_OFFICER',
  FINANCIAL_ADVISOR: 'FINANCIAL_ADVISOR',
  OPERATIONAL: 'OPERATIONAL',
  ANALYST: 'ANALYST',
  DISPUTE_OFFICER: 'DISPUTE_OFFICER'
}

export const userTypes = {
  NEXI: 'NEXI',
  ISP_NEXI: 'NEXI_ISP',
  ISP: 'ISP'
}

const userTypeMessages = defineMessages({
  ADMIN: {
    id: 't.app.settings.personalData.titlePage.admin',
    defaultMessage: 'Amministratore'
  },
  SECONDARY_OFFICER: {
    id: 't.app.secondaryUsers.role.secondaryOfficer.label',
    defaultMessage: 'Utente secondario'
  },
  FINANCIAL_ADVISOR: {
    id: 't.app.secondaryUsers.role.financialAdvisor.label',
    defaultMessage: 'Addetto alle dispute'
  },
  OPERATIONAL: {
    id: 't.app.secondaryUsers.role.operational.label',
    defaultMessage: 'Operativo'
  },
  ANALYST: {
    id: 't.app.secondaryUsers.role.analyst.label',
    defaultMessage: 'Analista'
  },
  DISPUTE_OFFICER: {
    id: 't.app.secondaryUsers.role.disputeOfficer.label',
    defaultMessage: 'Addetto alle dispute'
  },
  unknown: {
    id: 't.app.secondaryUsers.role.unknown',
    defaultMessage: 'Ruolo Sconosciuto'
  }
});
/**
 * Maps the user type to a formatted message using the intl object.
 * @param {string} userType - The user type.
 * @param {object} intl - The intl object for formatting messages.
 * @returns {string} - The formatted message corresponding to the user type.
 * @example
 * const userType = 'SECONDARY_OFFICER';
 * const message = mapUserType(userType, intl);
 * // message: 'incaricato secondario'
 */
export const mapUserType = (userType, intl) => {
  const userTypeMapping = {
    ADMIN: intl.formatMessage(userTypeMessages.ADMIN),
    SECONDARY_OFFICER: intl.formatMessage(userTypeMessages.SECONDARY_OFFICER),
    FINANCIAL_ADVISOR: intl.formatMessage(userTypeMessages.FINANCIAL_ADVISOR),
    OPERATIONAL: intl.formatMessage(userTypeMessages.OPERATIONAL),
    ANALYST: intl.formatMessage(userTypeMessages.ANALYST),
    DISPUTE_OFFICER: intl.formatMessage(userTypeMessages.DISPUTE_OFFICER),
  };

  return userTypeMapping[userType] || intl.formatMessage(userTypeMessages.unknown);
};

const shopTypeMessages = defineMessages({
  physicalStore: {
    id: 't.app.shopType.physicalStore',
    defaultMessage: 'Negozio Fisico'
  },
  ecommerce: {
    id: 't.app.shopType.ecommerce',
    defaultMessage: 'E-commerce'
  },
  payByLink: {
    id: 't.app.shopType.payByLink',
    defaultMessage: 'Pay-By-Link'
  },
  core: {
    id: 't.app.shopType.core',
    defaultMessage: 'Core'
  },
  mixed: {
    id: 't.app.shopType.mixed',
    defaultMessage: 'Misto'
  },
  unknown: {
    id: 't.app.shopType.unknown',
    defaultMessage: 'Tipo Sconosciuto'
  }
});

/**
 * Maps the shop type to a formatted message using the intl object.
 * @param {string} shopType - The shop type (P, E, PBL).
 * @param {object} intl - The intl object for formatting messages.
 * @returns {string} - The formatted message corresponding to the shop type.
 * @example
 * const shopType = 'P';
 * const message = mapShopType(shopType, intl);
 * // message: 'Negozio Fisico'
 */
export const mapShopType = (shopType, intl) => {
  const shopTypeMapping = {
    P: intl.formatMessage(shopTypeMessages.physicalStore),
    E: intl.formatMessage(shopTypeMessages.ecommerce),
    PBL: intl.formatMessage(shopTypeMessages.payByLink),
    C: intl.formatMessage(shopTypeMessages.core),
    M: intl.formatMessage(shopTypeMessages.mixed)
  };

  return shopTypeMapping[shopType] || intl.formatMessage(shopTypeMessages.unknown);
};

/**
 * 
 * Return status label and style state
 */
export function parseStatus(status) {
  switch (status) {
    case 'IN LAVORAZIONE':
      return { statusId: 't.app.badge.workInProgress', statusState: 'warning' }
    case 'INSTALLATO':
      return { statusId: 't.app.utils.parseStatus.installed', statusState: 'success' }
    case 'NON_COMPLETATO':
      return { statusId: 't.app.utils.parseStatus.failed', statusState: 'fail' }
    case 'IN_PROGRESS':
      return { statusId: 't.app.badge.workInProgress', statusState: 'warning' }
    case 'INSTALLED':
      return { statusId: 't.app.utils.parseStatus.installed', statusState: 'success' }
    case 'NOT_COMPLETED':
      return { statusId: 't.app.badge.workInProgress', statusState: 'warning' }
    default:
      return { statusId: 't.app.badge.workInProgress', statusState: 'warning' }
  }
};

const showFiltersMessages = defineMessages({
  showAllAlerts: {
    id: 't.app.alerts.filter.showAll',
    defaultMessage: 'Vedi tutti'
  },
  showUnreadAlerts: {
    id: 't.app.alerts.filter.unread',
    defaultMessage: 'Solo non letti'
  },
  showAllComunications: {
    id: 't.app.comunications.filter.showAll',
    defaultMessage: 'Vedi tutte'
  },
  showUnreadComunications: {
    id: 't.app.comunications.filter.unread',
    defaultMessage: 'Solo non lette'
  },
  unknown: {
    id: 't.app.comunications.alerts.filter.unknown',
    defaultMessage: 'Sconosciuto'
  }
});

/**
 * Maps the show filter to a formatted message using the intl object.
 * @param {string} showFilter - The show filter (ALL, UNREAD).
 * @param {string} type - The type of filter (alerts, comunications).
 * @param {object} intl - The intl object for formatting messages.
 * @returns {string} - The formatted message corresponding to the show filter.
 * @example
 * const showFilter = 'ALL';
 * const message = mapShowFilter(showFilter, 'alerts', intl);
 * // message: 'Vedi tutti'
 * @example
 * const showFilter = 'UNREAD';
 * const message = mapShowFilter(showFilter, 'comunications', intl);
 * // message: 'Solo non lette'
 * @example
 * const showFilter = 'NEW';
 * const message = mapShowFilter(showFilter, 'alerts', intl);
 * // message: 'Solo non letti'
  */
export const mapShowFilter = (showFilter, type, intl) => {
  const showFilterMapping = {
    ALL: type === 'alerts' ? intl.formatMessage(showFiltersMessages.showAllAlerts) : intl.formatMessage(showFiltersMessages.showAllComunications),
    UNREAD: type === 'alerts' ? intl.formatMessage(showFiltersMessages.showUnreadAlerts) : intl.formatMessage(showFiltersMessages.showUnreadComunications),
    NEW: type === 'alerts' ? intl.formatMessage(showFiltersMessages.showUnreadAlerts) : intl.formatMessage(showFiltersMessages.showUnreadComunications)
  };

  return showFilterMapping[showFilter] || intl.formatMessage(showFiltersMessages.unknown);
};

const comunicationsCategoryMessages = defineMessages({
  alerts: {
    id: 't.app.comunicationsCategory.alerts',
    defaultMessage: 'Alerts'
  },
  messages: {
    id: 't.app.comunicationsCategory.messages',
    defaultMessage: 'Messaggi'
  },
  news: {
    id: 't.app.comunicationsCategory.news',
    defaultMessage: 'News'
  },
  commercial: {
    id: 't.app.comunicationsCategory.commercial',
    defaultMessage: 'Commerciale'
  },
  legal: {
    id: 't.app.comunicationsCategory.legal',
    defaultMessage: 'Legale'
  },
  technical: {
    id: 't.app.comunicationsCategory.technical',
    defaultMessage: 'Tecnico'
  },
  fraudPrevention: {
    id: 't.app.comunicationsCategory.fraudPrevention',
    defaultMessage: 'Antiriciclaggio'
  },
  other: {
    id: 't.app.comunicationsCategory.other',
    defaultMessage: 'Altro'
  }
});

export const mapFilterCategory = (category, intl) => {
  const categoryMapping = {
    ALERTS: intl.formatMessage(comunicationsCategoryMessages.alerts),
    MESSAGES: intl.formatMessage(comunicationsCategoryMessages.messages),
    NEWS: intl.formatMessage(comunicationsCategoryMessages.news),
    COMMERCIAL: intl.formatMessage(comunicationsCategoryMessages.commercial),
    LEGAL: intl.formatMessage(comunicationsCategoryMessages.legal),
    TECHNICAL: intl.formatMessage(comunicationsCategoryMessages.technical),
    FRAUD_PREVENTION: intl.formatMessage(comunicationsCategoryMessages.fraudPrevention),
    AML_REMINDER: intl.formatMessage(comunicationsCategoryMessages.fraudPrevention),
    OTHER: intl.formatMessage(comunicationsCategoryMessages.other),
  };
  return categoryMapping[category] || intl.formatMessage(comunicationsCategoryMessages.other);
};

/**
 * Convert an array of string into an object based on the initialFilterType.
 * @param {Object} initialFilterType - The initial filter type object with default values.
 * @param {Array} filterArray - The array of selected elements.
 * @returns {Object} - The resulting filter object with keys from initialFilterType and boolean values indicating selection.
 * @example
 * const initialFilterType = { P: false, E: false, PBL: false };
 * const filterArray = ['P', 'PBL'];
 * const result = convertPreSelectFilters(initialFilterType, filterArray);
 * // result: { P: true, E: false, PBL: true }
 */
export const convertPreSelectFilters = (initialFilterType, filterArray) => {
  const filterObject = {};

  for (let key in initialFilterType) {
    filterObject[key] = filterArray.includes(key);
  }

  return filterObject;
};

export const isEmptyArray = (array) => Array.isArray(array) && array.length === 0;

const utilsMessages = defineMessages({
  formatITA: {
    id: 't.app.utils.format.ITA',
    defaultMessage: 'Italia'
  }
});

/**  @function
 * capitalizeAllString
 * @param {string} str
 * @returns {string}
 * @description Capitalize all words in a string
 * @example capitalizeAllString('HELLO, world') // Hello, World
 */
export const capitalizeAllString = (str) => str && str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());

/**  @function
 * getFormattedAddress
 * @param {object} address
 * @param {object} intl
 * @returns {string}
 * @description Get formatted address
 * @example getFormattedAddress({street: 'via roma, 1', city: 'roma', province: 'rm', postIndex: '00100', country: 'ITA'}, intl) // Via Roma 1, 00100 Roma RM, Italia
 */
export const getFormattedAddress = (address, intl) => {
  if (address) {
    const street = address.street ? capitalizeAllString(address.street.replace(/,/g, ' ')) : '';
    const city = address.city ? capitalizeAllString(address.city) : '';
    const province = address.province ? address.province.toUpperCase() : '';
    const postIndex = address.postIndex ? address.postIndex : '';
    const country = address.country
      ? address.country === 'ITA'
        ? intl.formatMessage(utilsMessages.formatITA)
        : address.country
      : '';

    const fullAddress = `${street ? `${street}, ` : ''}${postIndex} ${city} ${province}`.trim();

    if (country) {
      if (fullAddress) {
        return `${fullAddress}, ${country}`;
      } else {
        return `${country}`;
      }
    } else {
      return fullAddress;
    }
  } else {
    return '';
  }
};

export const toTitleCaseAddress = (str) => {
  // Assicura che dopo ogni virgola ci sia un solo spazio
  str = str.replace(/,\s*/g, ', ');
  return str.toLowerCase().split(' ').map(function (word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
}

export const areObjectsDifferent = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return true;
  }

  return keys1.some(key => object1[key] !== object2[key]);
};

/**
 * Updates a specified property for the element at the given index.
 * @param {number} id - The index of the customArray element to update.
 * @param {Array} customArray - The array of objects to update.
 * @param {string} prop - The property to update.
 * @param {*} value - The new value to set for the property.
 * @returns {Array} - The updated array of objects.
 * @example
 * const customArray = [{ id: 1, newMessage: true }, { id: 2, newMessage: false }];
 * const updatedMessages = updateMessageProperty(1, customArray, 'newMessage', false);
 * // updatedMessages: [{ id: 1, newMessage: true }, { id: 2, newMessage: false }]
 */
export const updateArrayProperty = (id, customArray, prop, value) => {
  if (id >= 0 && id < customArray.length) {
    return customArray.map((element, index) => {
      if (index === id) {
        return { ...element, [prop]: value };
      }
      return element;
    });
  }
  return customArray;
};

/**
 * Formats an address string by properly capitalizing words and handling special cases.
 * @param {string} address - The address string to format (e.g., "VIA PIETRO BIGNARDI 35, SNC").
 * @returns {string} - The formatted address (e.g., "Via Pietro Bignardi 35, SNC").
 * @example
 * const address = "VIA PIETRO BIGNARDI 35, SNC";
 * const formatted = formatAddress(address);
 * // formatted: "Via Pietro Bignardi 35, SNC"
 */
export const formatStringAddress = (address) => {
  if (!address) {
    return address;
  }

  const abbreviations = ['SNC', 'SRL', 'SPA', 'SAS', 'SS', 'SDF', 'SC', 'SCARL'];
  const lowercaseWords = ['di', 'da', 'del', 'della', 'dei', 'delle', 'dal', 'dalla', 'dai', 'dalle', 'al', 'alla', 'agli', 'alle', 'con', 'per', 'tra', 'fra', 'in', 'su', 'a', 'e', 'o'];

  return address
    .toLowerCase()
    .split(/(\s+|,\s*)/) // spazi e virgole come separatori
    .map((part, index) => {
      // Se è solo spazio o virgola, restituisci così com'è
      if (/^\s*,?\s*$/.test(part)) {
        return part;
      }

      const trimmedPart = part.trim();

      if (!trimmedPart) {
        return part;
      }

      if (abbreviations.includes(trimmedPart.toUpperCase())) {
        return trimmedPart.toUpperCase();
      }

      if (index > 0 && lowercaseWords.includes(trimmedPart.toLowerCase())) {
        return trimmedPart.toLowerCase();
      }

      if (trimmedPart.length === 2) {
        return trimmedPart.toUpperCase();
      }

      return trimmedPart.charAt(0).toUpperCase() + trimmedPart.slice(1).toLowerCase();
    })
    .join('');
};

// Main routes list used to reset selectedTabs on main route change
// Update this list in base of new needed main routes
export const MAIN_ROUTES_RESET_TABS = [
  '', // HOME
  'transactions',
  'documents',
  'costs',
  'services',
  'results',
  'profile',
  'support',
  'pay-by-link',
  'dispute'
]
